import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSearch,
  faFilter,
  faHandsHelping,
  faFlask,
  faCircleExclamation,
  faCircleCheck,
  faXmark,
  faEye,
  faStore,
  faLocationDot,
  faStar,
  faSortAmountDown,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const HerKartServices = () => {
  const navigate = useNavigate();
  const { fetchLiveServices, submitSampleRequest } = useHerKart();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("featured");
  
  const [demoFormFor, setDemoFormFor] = useState(null); // service object, or null
  const [demoNote, setDemoNote] = useState("");
  const [submittingDemo, setSubmittingDemo] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetchLiveServices();
      if (response?.success) {
        setServices(response.services || []);
      }
    } catch (e) {
      console.error("Services fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => {
    const unique = [...new Set(services.map((s) => s.category).filter(Boolean))];
    return ["All", ...unique];
  }, [services]);

  const filteredServices = useMemo(() => {
    let list = [...services];

    if (selectedCategory !== "All") {
      list = list.filter((s) => s.category === selectedCategory);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((s) =>
        [s.name, s.category, s.description].join(" ").toLowerCase().includes(keyword)
      );
    }

    switch (sortBy) {
      case "name":
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;

      case "nameReverse":
        list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;

      case "priceLow":
        list.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;

      case "priceHigh":
        list.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;

      case "city":
        list.sort((a, b) => (a.city || "").localeCompare(b.city || ""));
        break;

      default:
        break;
    }

    return list;
  }, [services, search, selectedCategory, sortBy]);

  const openDemoForm = (service) => {
    setDemoFormFor(service);
    setDemoNote("");
    setMessage(null);
  };

  const closeDemoForm = () => {
    setDemoFormFor(null);
    setDemoNote("");
  };

  const submitDemoRequest = async (e) => {
    e.preventDefault();
    if (!demoFormFor) return;

    setSubmittingDemo(true);
    try {
      const response = await submitSampleRequest({
        publisherCode: demoFormFor.sellerCode,
        type: "Demo",
        refCode: String(demoFormFor._id),
        refName: demoFormFor.name,
        note: demoNote.trim(),
      });

      if (response?.success) {
        setMessage({ type: "success", text: "Demo request sent! The seller will get in touch soon." });
        closeDemoForm();
      } else {
        setMessage({ type: "error", text: "Failed to send demo request." });
      }
    } catch (e) {
      console.error("Demo request error:", e);
      setMessage({ type: "error", text: "Failed to send demo request." });
    } finally {
      setSubmittingDemo(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/foundher"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 transition"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-xl font-bold">Her Services</h1>
              <p className="text-xs text-slate-400">Support & services from women entrepreneurs</p>
            </div>
          </div>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-7xl px-4 pt-4">
          <div
            className={`flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon icon={message.type === "success" ? faCircleCheck : faCircleExclamation} />
            {message.text}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 py-5"></div>
      <div className="mx-auto max-w-7xl px-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search services, categories..."
              className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-pink-500 focus:outline-none"
            />
          </div>

          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <div className="flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-3 py-2 text-xs text-pink-300 whitespace-nowrap">
              <FontAwesomeIcon icon={faFilter} />
              Categories
            </div>

            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`whitespace-nowrap rounded-full w-fit px-2 py-1 text-xs lg:text-sm transition ${
                  selectedCategory === category
                    ? "bg-pink-600 text-white"
                    : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="mt-2 flex flex-row gap-3 justify-end">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <FontAwesomeIcon icon={faSortAmountDown} className="text-pink-400" />
              <span>Sort By</span>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-xl border border-white/10 bg-[#0b1120] w-fit px-2 py-1 text-sm text-white focus:border-pink-500 focus:outline-none"
            >
              <option value="featured">Featured</option>
              <option value="name">Name (A-Z)</option>
              <option value="nameReverse">Name (Z-A)</option>
              <option value="priceLow">Price (Low-High)</option>
              <option value="priceHigh">Price (High-Low)</option>
              <option value="city">City</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="mt-16 text-center text-slate-400">Loading services...</div>
        ) : filteredServices.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon icon={faHandsHelping} className="mx-auto mb-4 text-4xl text-slate-500" />
            <h3 className="text-xl font-semibold">No services found</h3>
            <p className="mt-2 text-sm text-slate-400">
              {services.length === 0
                ? "No services have been listed yet. Check back soon!"
                : "Try changing your search or category."}
            </p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredServices.map((service) => (
              <div
                key={service._id}
                className="rounded-3xl border border-white/10 bg-white/5 p-5 transition hover:border-pink-500/40"
              >
                <img
                  src={service.image || "https://placehold.co/400x250/18181b/f9a8d4?text=Service"}
                  alt={service.name}
                  className="mb-4 h-40 w-full rounded-2xl object-cover"
                />

                <span className="inline-flex rounded-full bg-pink-600/20 px-3 py-1 text-xs font-semibold text-pink-300">
                  {service.category || "Service"}
                </span>

                <h3 className="mt-3 text-lg font-bold">{service.name}</h3>

                <div className="mt-1 flex items-center gap-1 text-xs text-amber-400">
                  <FontAwesomeIcon icon={faStar} />
                  <span className="font-semibold">{service.rating || 0}</span>
                  <span className="text-slate-500">({service.totalRatings || 0})</span>
                </div>

                {(service.entrepreneur || service.store) && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                    <FontAwesomeIcon icon={faStore} className="text-pink-400" />
                    <span className="truncate">{service.entrepreneur || service.store}</span>
                  </div>
                )}

                {(service.locality || service.city) && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                    <FontAwesomeIcon icon={faLocationDot} className="text-pink-400" />
                    <span>
                      {service.locality}
                      {service.locality && service.city ? ", " : ""}
                      {service.city}
                    </span>
                  </div>
                )}

                {service.description && (
                  <p className="mt-2 text-sm text-slate-300 line-clamp-3">{service.description}</p>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xl font-bold text-pink-400">₹{service.price}</div>
                  {service.duration && <div className="text-xs text-slate-400">{service.duration}</div>}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => navigate(`/herkart/services/${service._id}`)}
                    className="flex w-fit items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-pink-600 to-fuchsia-600 px-3 py-2 text-sm font-semibold hover:scale-[1.02] transition"
                  >
                    <FontAwesomeIcon icon={faEye} />
                    View & Book
                  </button>

                  <button
                    onClick={() => openDemoForm(service)}
                    className="flex w-fit px-3 py-2 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 text-sm font-semibold hover:bg-white/10"
                  >
                    <FontAwesomeIcon icon={faFlask} />
                    Request Demo
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {demoFormFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0b1120] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Request Demo — {demoFormFor.name}</h3>
              <button onClick={closeDemoForm} className="text-slate-400 hover:text-white w-fit px-2 py-1">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form onSubmit={submitDemoRequest}>
              <label className="mb-2 block text-sm text-slate-300">Note (optional)</label>
              <textarea
                value={demoNote}
                onChange={(e) => setDemoNote(e.target.value.slice(0, 250))}
                style={{height: "100px"}}
                maxLength={250}
                placeholder="Preferred time, specific questions..."
                className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm outline-none focus:border-pink-500"
              />
              <div className="mt-1 text-right text-xs text-slate-500">{demoNote.length}/250</div>

              <div className="mt-4 flex gap-3">
                <button
                  type="submit"
                  disabled={submittingDemo}
                  className="flex-1 rounded-xl bg-pink-600 w-fit px-2 py-1 font-semibold hover:bg-pink-500 disabled:opacity-60"
                >
                  {submittingDemo ? "Sending..." : "Send Request"}
                </button>
                <button
                  type="button"
                  onClick={closeDemoForm}
                  className="flex-1 rounded-xl border border-white/10 w-fit px-2 py-1 hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerKartServices;